import {
  ActionPalette,
  BrandPalette,
  ErrorPalette,
  GrayscalePalette,
  PrimaryBluePalette,
  SuccessPalette,
  WarningPalette,
} from '@tcl-boron-colors/colors';
import { theme, type ThemeConfig } from 'antd';

/**
 * Seed + component tokens that approximate packaged `@tcl-boron-prefabs/*`.
 *
 * Packaged TCL is light-only and is the color source of truth. Dark (Boron)
 * inverts neutrals (navy canvas, light type). Primary fills use Action blue
 * because packaged navy disappears on that canvas. Semantic hues are pinned
 * after Ant’s dark algorithm so they are not remapped.
 *
 * Closest simple approximations (do not copy packaged quirks literally):
 * - Focus is a 3px outline (`controlOutline`), not Boron’s padding-shifting border.
 * - Control type stays 14px (packaged placeholders are 12px).
 * - Body text is Brand `#29293D` on light, white on dark (packaged mixes `#000`).
 * - Switch / slider handle sizes stay Ant defaults; colors stay packaged.
 */
export type BoronMode = 'light' | 'dark';

const FONT_FAMILY = "'IBM Plex Sans', Helvetica, sans-serif";
const LIGHT_INPUT_SHADOW = `0px 1px 3px 1px ${GrayscalePalette[300]}`;
const DARK_INPUT_SHADOW = `0px 1px 3px 1px ${GrayscalePalette[650]}`;

const lightInputLikeTokens = {
  activeBg: GrayscalePalette[200],
  activeBorderColor: ActionPalette[500],
  activeShadow: LIGHT_INPUT_SHADOW,
  hoverBg: GrayscalePalette[100],
  hoverBorderColor: ActionPalette[500],
  paddingInline: 16,
};

const darkInputLikeTokens = {
  activeBg: GrayscalePalette[650],
  activeBorderColor: ActionPalette[500],
  activeShadow: DARK_INPUT_SHADOW,
  hoverBg: BrandPalette[700],
  hoverBorderColor: ActionPalette[500],
  paddingInline: 16,
};

export const getBoronSeedToken = (mode: BoronMode): NonNullable<ThemeConfig['token']> =>
  mode === 'dark'
    ? {
        borderRadius: 20,
        boxShadow: DARK_INPUT_SHADOW,
        colorBgBase: BrandPalette[700],
        colorBgContainer: BrandPalette[700],
        colorBgLayout: GrayscalePalette[650],
        colorBorder: GrayscalePalette[500],
        colorError: ErrorPalette[700],
        colorInfo: ActionPalette[300],
        colorLink: PrimaryBluePalette[400],
        // Packaged primary navy is unusable on a navy canvas; Action blue is the package accent.
        colorPrimary: ActionPalette[300],
        colorSuccess: SuccessPalette[700],
        colorTextBase: GrayscalePalette[100],
        colorWarning: WarningPalette[700],
        controlHeight: 40,
        controlOutline: ActionPalette[300],
        controlOutlineWidth: 3,
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        fontWeightStrong: 700,
        lineWidth: 1,
      }
    : {
        borderRadius: 20,
        boxShadow: LIGHT_INPUT_SHADOW,
        colorBgBase: GrayscalePalette[100],
        colorBgContainer: GrayscalePalette[100],
        colorBgLayout: GrayscalePalette[200],
        colorBorder: GrayscalePalette[400],
        colorError: ErrorPalette[700],
        colorInfo: ActionPalette[300],
        colorLink: ActionPalette[300],
        colorPrimary: ActionPalette[600],
        colorSuccess: SuccessPalette[700],
        colorTextBase: BrandPalette[700],
        colorWarning: WarningPalette[700],
        controlHeight: 40,
        controlOutline: ActionPalette[300],
        controlOutlineWidth: 3,
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        fontWeightStrong: 700,
        lineWidth: 1,
      };

/**
 * Dark algorithm for inverted neutrals, with packaged semantic colors pinned.
 * Ant would otherwise shift Action blue (`#206EF3` → `#1E61D2`).
 */
export const boronDarkAlgorithm: NonNullable<ThemeConfig['algorithm']> = (seed) => {
  const derived = theme.darkAlgorithm(seed);

  return {
    ...derived,
    colorBgBase: seed.colorBgBase.toLowerCase(),
    colorError: seed.colorError.toLowerCase(),
    colorInfo: seed.colorInfo.toLowerCase(),
    colorLink: seed.colorLink.toLowerCase(),
    colorPrimary: seed.colorPrimary.toLowerCase(),
    colorSuccess: seed.colorSuccess.toLowerCase(),
    colorTextBase: seed.colorTextBase.toLowerCase(),
    colorWarning: seed.colorWarning.toLowerCase(),
  };
};

export const getBoronHighContrastToken = (mode: BoronMode): NonNullable<ThemeConfig['token']> => ({
  ...getBoronSeedToken(mode),
  controlOutlineWidth: 3,
  lineWidth: 2,
  wireframe: true,
});

export const getBoronComponentTokens = (mode: BoronMode): NonNullable<ThemeConfig['components']> => {
  const isDark = mode === 'dark';

  return {
    Alert: {
      borderRadius: 8,
      defaultPadding: '18px 16px 18px 24px',
    },
    Badge: {
      textFontSize: 12,
      textFontWeight: 700,
    },
    Breadcrumb: {
      fontSize: 12,
      itemColor: isDark ? GrayscalePalette[400] : GrayscalePalette[500],
      lastItemColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      linkColor: isDark ? GrayscalePalette[400] : GrayscalePalette[500],
      linkHoverColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      separatorMargin: 12,
    },
    Button: isDark
      ? {
          borderRadius: 25,
          colorPrimary: ActionPalette[300],
          colorPrimaryActive: PrimaryBluePalette[300],
          colorPrimaryHover: PrimaryBluePalette[400],
          contentFontSize: 14,
          controlHeight: 48,
          controlHeightSM: 32,
          defaultActiveBg: GrayscalePalette[650],
          defaultActiveBorderColor: ActionPalette[500],
          defaultActiveColor: GrayscalePalette[100],
          defaultBg: 'transparent',
          defaultBgDisabled: GrayscalePalette[650],
          defaultBorderColor: GrayscalePalette[100],
          defaultColor: GrayscalePalette[100],
          defaultHoverBg: ActionPalette[500],
          defaultHoverBorderColor: ActionPalette[500],
          defaultHoverColor: ActionPalette[100],
          defaultShadow: 'none',
          fontWeight: 700,
          lineWidth: 2,
          paddingInline: 24,
          paddingInlineSM: 24,
          primaryColor: GrayscalePalette[100],
          primaryShadow: 'none',
        }
      : {
          borderRadius: 25,
          colorPrimary: ActionPalette[600],
          colorPrimaryActive: ActionPalette[600],
          colorPrimaryHover: ActionPalette[500],
          contentFontSize: 14,
          controlHeight: 48,
          controlHeightSM: 32,
          defaultActiveBg: ActionPalette[200],
          defaultActiveBorderColor: ActionPalette[500],
          defaultActiveColor: ActionPalette[600],
          defaultBg: 'transparent',
          defaultBgDisabled: ActionPalette[200],
          defaultBorderColor: ActionPalette[600],
          defaultColor: ActionPalette[600],
          defaultHoverBg: ActionPalette[500],
          defaultHoverBorderColor: ActionPalette[500],
          defaultHoverColor: ActionPalette[100],
          defaultShadow: 'none',
          fontWeight: 700,
          lineWidth: 2,
          paddingInline: 24,
          paddingInlineSM: 24,
          primaryColor: GrayscalePalette[100],
          primaryShadow: 'none',
        },
    Checkbox: {
      borderRadiusSM: 2,
    },
    Divider: {
      colorSplit: isDark ? GrayscalePalette[500] : GrayscalePalette[300],
    },
    Input: {
      ...(isDark ? darkInputLikeTokens : lightInputLikeTokens),
      borderRadius: 20,
      controlHeight: 40,
    },
    Pagination: {
      borderRadius: 10,
      itemSize: 40,
    },
    Progress: {
      defaultColor: isDark ? ActionPalette[300] : ActionPalette[600],
      lineBorderRadius: 2,
      remainingColor: isDark ? ActionPalette[500] : ActionPalette[200],
    },
    Select: {
      ...(isDark ? darkInputLikeTokens : lightInputLikeTokens),
      borderRadius: 20,
      controlHeight: 40,
    },
    Slider: {
      handleColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      railBg: isDark ? GrayscalePalette[500] : GrayscalePalette[300],
      trackBg: isDark ? GrayscalePalette[100] : BrandPalette[700],
      trackHoverBg: isDark ? GrayscalePalette[100] : BrandPalette[700],
    },
    Switch: {
      colorPrimary: ActionPalette[300],
      colorPrimaryHover: ActionPalette[300],
      handleBg: ActionPalette[100],
      handleShadow: `0 1px 2px 0 ${ActionPalette[500]}`,
    },
    Table: {
      borderColor: isDark ? GrayscalePalette[500] : GrayscalePalette[300],
      headerBg: isDark ? GrayscalePalette[650] : GrayscalePalette[200],
      headerColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
    },
    Tabs: {
      inkBarColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      itemActiveColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      itemColor: isDark ? GrayscalePalette[400] : GrayscalePalette[500],
      itemHoverColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      itemSelectedColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
      titleFontSize: 14,
    },
    Tag: {
      borderRadiusSM: 16,
      defaultBg: isDark ? GrayscalePalette[650] : ActionPalette[200],
      defaultColor: isDark ? GrayscalePalette[100] : BrandPalette[700],
    },
    Tooltip: {
      colorBgSpotlight: ActionPalette[100],
      colorTextLightSolid: BrandPalette[700],
    },
    Typography: {
      fontFamily: FONT_FAMILY,
    },
  };
};
